class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.debounceRefresh = this.debounce(this.forceCartRefresh.bind(this), 300);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initQuickAdd();
  }

  // Utility to debounce cart refresh
  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  initQuickAdd() {
    document.addEventListener('click', async (event) => {
      const quickAddBtn = event.target.closest('[data-quick-add-btn], .product-form__submit[name="add"]');
      if (!quickAddBtn) return;

      event.preventDefault();
      const form = quickAddBtn.closest('form');
      if (form) {
        // Prevent form submission
        form.addEventListener('submit', (submitEvent) => {
          submitEvent.preventDefault();
        }, { once: true });
        await this.handleQuickAdd(form, quickAddBtn);
      }
    });
  }

  async handleQuickAdd(form, button) {
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');

    try {
      // 1. Add item to cart
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: form.querySelector('[name="id"]').value,
            quantity: 1
          }]
        })
      });

      const addJson = await addResponse.json();
      if (addJson.status === 'error') throw new Error(addJson.description);

      // 2. Refresh cart content
      await this.debounceRefresh();

      this.showSuccess(button);

      // 3. Open cart drawer
      this.open(button);
      console.debug('Cart drawer opened after quick add');

    } catch (error) {
      console.error('Error in handleQuickAdd:', error);
      this.showError(form, error);
    } finally {
      setTimeout(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
      }, 1000);
    }
  }

  async forceCartRefresh() {
    try {
      // 1. Fetch cart data and sections
      const cacheBust = `t=${Date.now()}`;
      const sectionPromise = fetch(`${window.Shopify.routes.root}?section_id=cart-drawer&${cacheBust}`);
      const cartJsonPromise = fetch(`${window.Shopify.routes.root}cart.js?${cacheBust}`);

      const [sectionResponse, cartJsonResponse] = await Promise.all([sectionPromise, cartJsonPromise]);

      // Parse cart JSON
      let cartData;
      if (cartJsonResponse.ok) {
        cartData = await cartJsonResponse.json();
      } else {
        throw new Error('Failed to fetch cart.js');
      }

      // Parse section response
      let sections = {};
      if (sectionResponse.ok) {
        const text = await sectionResponse.text();
        sections['cart-drawer'] = text;
      } else {
        console.warn('Section fetch failed, using cart.js data');
        sections['cart-drawer'] = this.renderCartItems(cartData);
      }

      // 2. Update cart drawer
      this.renderContents({
        id: cartData.items.length ? cartData.items[0].product_id : null,
        sections,
        items: cartData.items
      });

      // 3. Fix image overflow
      this.fixImageOverflow(this);

      // 4. Update cart count bubble
      const cartCountBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (cartCountBubble) {
        cartCountBubble.innerHTML = `<span>${cartData.item_count}</span>`;
      }

      // 5. Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          source: 'upsell',
          forceRefresh: true,
          cartState: cartData.items.length ? 'non-empty' : 'empty'
        }
      }));

    } catch (error) {
      console.error('Error forcing cart refresh:', error);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const isEmpty = !parsedState.items.length;
    const innerContainer = this.querySelector('.drawer__inner, .drawer__inner-empty');
    if (innerContainer) {
      innerContainer.classList.remove('drawer__inner', 'drawer__inner-empty');
      innerContainer.classList.add(isEmpty ? 'drawer__inner-empty' : 'drawer__inner');
    }
    this.classList.toggle('is-empty', isEmpty);

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    // Reinitialize custom elements
    this.reinitializeCustomElements();

    // Update cart heading
    const heading = this.querySelector('.drawer__heading');
    if (heading) {
      heading.textContent = `Your Cart – ${parsedState.items.length}`;
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  fixImageOverflow(container) {
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      if (window.getComputedStyle(img).overflow === 'visible') {
        img.style.overflow = 'clip';
        console.debug('Fixed overflow on image:', img);
      }
    });
  }

  reinitializeCustomElements() {
    const quantityInputs = this.querySelectorAll('quantity-input');
    const removeButtons = this.querySelectorAll('cart-remove-button');

    quantityInputs.forEach(input => {
      if (!customElements.get('quantity-input')) {
        console.warn('quantity-input custom element not defined');
      } else if (!input.isConnected) {
        input.replaceWith(input.cloneNode(true));
      }
    });

    removeButtons.forEach(button => {
      if (!customElements.get('cart-remove-button')) {
        console.warn('cart-remove-button custom element not defined');
      } else if (!button.isConnected) {
        button.replaceWith(button.cloneNode(true));
      }
    });

    console.debug('Custom elements reinitialized:', { quantityInputs: quantityInputs.length, removeButtons: removeButtons.length });
  }

  showSuccess(button) {
    button.classList.add('success');
    setTimeout(() => {
      button.classList.remove('success');
    }, 2000);
  }

  showError(form, error) {
    const errorContainer = form.closest('[data-quick-add-holder]')?.querySelector('[data-cart-errors-container]');
    if (errorContainer) {
      errorContainer.innerHTML = `<div class="errors">${error.message || 'Something went wrong. Please try again.'}</div>`;
      errorContainer.style.maxHeight = `${errorContainer.scrollHeight}px`;
      errorContainer.style.opacity = '1';
      errorContainer.style.visibility = 'visible';

      setTimeout(() => {
        errorContainer.style.maxHeight = '0';
        errorContainer.style.opacity = '0';
        errorContainer.style.visibility = 'hidden';
      }, 5000);
    }
  }

  renderCartItems(cartData) {
    if (!cartData.items.length) {
      return '<div class="drawer__inner-empty"><p>Your cart is empty.</p></div>';
    }
    return `
      <div class="drawer__inner">
        <div class="drawer__cart-items-wrapper">
          <table class="cart-items" role="table">
            <tbody role="rowgroup">
              ${cartData.items.map((item, index) => `
                <tr id="CartDrawer-Item-${index + 1}" class="cart-item" role="row">
                  <td class="cart-item__media" role="cell">
                    <a href="${item.url}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>
                    <img class="cart-item__image" src="${item.image}" alt="${item.title}" loading="lazy" width="150">
                  </td>
                  <td class="cart-item__details" role="cell">
                    <a href="${item.url}" class="cart-item__name h4 break" style="font-size: 14px">${item.title}</a>
                    <div class="product-option">€${(item.final_price / 100).toFixed(2)}</div>
                  </td>
                  <td class="cart-item__totals right" role="cell">
                    <div class="cart-item__quantity">
                      <quantity-popover>
                        <div class="cart-drawer-quantity-wrapper">
                          <quantity-input class="quantity cart-quantity">
                            <button class="quantity__button" name="minus" type="button">
                              <span class="visually-hidden">Decrease quantity</span>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V7H0V5H12Z" fill="currentColor"></path>
                              </svg>
                            </button>
                            <input class="quantity__input" type="number" data-quantity-variant-id="${item.variant_id}" name="updates[]" value="${item.quantity}" data-cart-quantity="${item.quantity}" min="1" step="1" aria-label="Quantity for ${item.title}" id="Drawer-quantity-${index + 1}" data-index="${index + 1}">
                            <button class="quantity__button" name="plus" type="button">
                              <span class="visually-hidden">Increase quantity</span>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 0H7V12H5V0Z" fill="currentColor"></path>
                                <path d="M12 5V7H0V5H12Z" fill="currentColor"></path>
                              </svg>
                            </button>
                          </quantity-input>
                        </div>
                        <cart-remove-button id="CartDrawer-Remove-${index + 1}" data-index="${index + 1}">
                          <button type="button" class="button button--tertiary cart-remove-button" aria-label="Remove ${item.title}" data-variant-id="${item.variant_id}">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon icon-remove">
                              <path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000 1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path>
                              <path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"></path>
                            </svg>
                          </button>
                        </cart-remove-button>
                      </quantity-popover>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);